import type React from "react";
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

export interface CommandAction {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  onSelect: () => void;
  keywords?: string[];
  destructive?: boolean;
}

export interface CommandGroup {
  id: string;
  heading: string;
  actions: CommandAction[];
}

interface ContextConfig {
  label?: string;
  badge?: string;
  groups?: CommandGroup[];
}

interface CommandBarContextType {
  isOpen: boolean;
  searchQuery: string;
  contextConfig: ContextConfig | null;
  searchResults: CommandAction[];
  setSearchQuery: (query: string) => void;
  openCommandBar: () => void;
  closeCommandBar: () => void;
  setContextConfig: (config: ContextConfig | null) => void;
  clearContext: () => void;
  setSearchResults: (results: CommandAction[]) => void;
}

const CommandBarContext = createContext<CommandBarContextType | undefined>(
  undefined,
);

export function CommandBarProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [contextConfig, setContextConfig] = useState<ContextConfig | null>(
    null,
  );
  const [searchResults, setSearchResults] = useState<CommandAction[]>([]);

  const openCommandBar = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeCommandBar = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
    setSearchResults([]);
  }, []);

  const clearContext = useCallback(() => {
    setContextConfig(null);
  }, []);

  // Global Cmd+K listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (isOpen) {
          closeCommandBar();
        } else {
          openCommandBar();
        }
      }
      if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        closeCommandBar();
        clearContext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeCommandBar, openCommandBar, clearContext]);

  return (
    <CommandBarContext.Provider
      value={{
        isOpen,
        searchQuery,
        contextConfig,
        searchResults,
        setSearchQuery,
        openCommandBar,
        closeCommandBar,
        setContextConfig,
        clearContext,
        setSearchResults,
      }}
    >
      {children}
    </CommandBarContext.Provider>
  );
}

export function useCommandBar() {
  const context = useContext(CommandBarContext);
  if (context === undefined) {
    throw new Error("useCommandBar must be used within a CommandBarProvider");
  }
  return context;
}
